import {
  PaymentRow,
  PaymentRowType,
  Scenario,
  ScheduleType,
} from '../../shared/types.ts'
import { getRowPayoutValue } from './conversion-utils'

interface PaymentScheduleRow {
  payoutPercentage: number
  paymentDate: Date | string | null
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 * Validates individual payout percentage to ensure it's within 0-100%
 */
export const validatePayoutPercentage = (percentage: number): boolean => {
  return percentage >= 0 && percentage <= 100
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 * Validates payment dates are in ascending order
 */
export const validateDateOrder = (
  paymentSchedule: PaymentScheduleRow[]
): boolean => {
  for (let i = 1; i < paymentSchedule.length; i++) {
    const prevDate = paymentSchedule[i - 1].paymentDate
    const currDate = paymentSchedule[i].paymentDate

    if (prevDate != null && currDate != null) {
      const prev = new Date(prevDate).getTime()
      const curr = new Date(currDate).getTime()
      if (curr < prev) {
        return false
      }
    }
  }
  return true
}

/**
 * Validates scenario payment schedule to ensure:
 * - Percentages are within 0-100%
 * - Total exactly 100% (Deposit mode)
 * - All dates are set
 * - Dates are in ascending order
 * - No dates in the past
 */
export const validatePaymentSchedule = (
  paymentSchedule: PaymentScheduleRow[] | PaymentRow[],
  scheduleType: ScheduleType = ScheduleType.DEPOSIT,
  depositAmount?: number,
  enablePartialMaturitiesByDollarAmount: boolean = false
): string => {
  // Convert dollar amounts to percentages for validation
  const normalizedSchedule = paymentSchedule.map(
    (row: PaymentScheduleRow | PaymentRow) => {
      const isPaymentRow = 'payoutAmount' in row
      if (!isPaymentRow) {
        return row
      }

      const paymentRow = row
      const isPercentageMode =
        (paymentRow.inputType ?? PaymentRowType.PERCENTAGE) ===
        PaymentRowType.PERCENTAGE

      let percentage = getRowPayoutValue(
        paymentRow,
        enablePartialMaturitiesByDollarAmount
      )
      if (!isPercentageMode && depositAmount != null && depositAmount > 0) {
        // Convert dollar amount to percentage
        const amount = getRowPayoutValue(
          paymentRow,
          enablePartialMaturitiesByDollarAmount
        )
        percentage = (amount / depositAmount) * 100
      }

      return {
        payoutPercentage: percentage,
        paymentDate: paymentRow.paymentDate,
      }
    }
  )

  // Validate individual percentages are within 0-100%
  const hasInvalidPercentage = normalizedSchedule.some(
    (row) => row.payoutPercentage < 0 || row.payoutPercentage > 100
  )
  if (hasInvalidPercentage) {
    return 'Payment percentages must be between 0% and 100%'
  }

  // Validate no zero or negative percentages
  const hasZeroPercentage = normalizedSchedule.some(
    (row) => row.payoutPercentage <= 0
  )
  if (hasZeroPercentage) {
    return 'Payment percentages must be greater than 0'
  }

  // In Deposit mode, validate total equals 100%
  if (scheduleType === ScheduleType.DEPOSIT) {
    const totalPercentage =
      Math.round(
        normalizedSchedule.reduce(
          (sum: number, row) => sum + (row.payoutPercentage ?? 0),
          0
        ) * 100
      ) / 100

    // Use tolerance-based comparison to handle floating-point precision issues
    const tolerance = 0.001
    if (Math.abs(totalPercentage - 100) > tolerance) {
      return `Payment percentages must sum to exactly 100%`
    }
  }

  // Validate all dates are set
  const hasMissingDates = normalizedSchedule.some(
    (row) => row.paymentDate === null
  )
  if (hasMissingDates) {
    return 'All payment dates must be set'
  }

  // Validate dates are in ascending order
  if (!validateDateOrder(normalizedSchedule)) {
    return 'Payment dates must be in ascending order'
  }

  // Validate no past dates
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const hasPastDates = normalizedSchedule.some((row) => {
    if (row.paymentDate == null) return false
    const date = new Date(row.paymentDate)
    // Check for invalid dates or year 0000 (treat as past date)
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 0) return true
    return date < today
  })
  if (hasPastDates) {
    return 'All payment dates must be in the future'
  }

  return ''
}

/**
 * Validates all scenarios in a proposal
 */
export const validateScenarios = (
  scenarios: Scenario[],
  scheduleType: ScheduleType = ScheduleType.DEPOSIT,
  depositAmount?: number,
  enablePartialMaturitiesByDollarAmount: boolean = false
): { isValid: boolean; errors: Record<string, string> } => {
  if (scenarios.length === 0) {
    return {
      isValid: false,
      errors: { global: 'At least one scenario must be added' },
    }
  }

  const scenarioErrors: Record<string, string> = {}

  for (const scenario of scenarios) {
    const paymentSchedule = scenario.paymentSchedule

    if (paymentSchedule.length === 0) {
      scenarioErrors[scenario.id] = 'Must have at least one payment row'
      continue
    }

    const validationError = validatePaymentSchedule(
      paymentSchedule,
      scheduleType,
      depositAmount,
      enablePartialMaturitiesByDollarAmount
    )
    if (validationError !== '') {
      scenarioErrors[scenario.id] = validationError
    }
  }

  return {
    isValid: Object.keys(scenarioErrors).length === 0,
    errors: scenarioErrors,
  }
}
